import React, { useState, useEffect } from 'react';
import { useSafeThemeClasses } from '@/shared/theme';
import { adminApiService } from '@/shared/services/adminApiService';
import type { AdminBlog } from '@/shared/types/admin';
import { logger } from '@/shared/utils';
import toast from 'react-hot-toast';
import { dateUtils } from '@/shared/utils';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/shared/config';
import { useConfirmDialog } from '@/shared/components/ui';

const AdminBlogManagementPage: React.FC = () => {
  const theme = useSafeThemeClasses();
  const [blogs, setBlogs] = useState<AdminBlog[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'published' | 'draft' | 'archived'>('all');
  const { confirm, ConfirmDialog } = useConfirmDialog();

  useEffect(() => {
    loadBlogs();
  }, []);

  const loadBlogs = async () => {
    try {
      setLoading(true);
      const response = await adminApiService.getBlogs();
      if (response.success && response.data) {
        setBlogs(response.data.blogs || []);
      } else {
        toast.error(response.error?.message || 'Failed to load blogs');
        logger.error('Failed to load blogs', 'AdminBlogManagementPage', { error: response.error });
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
      logger.error('Network error loading blogs', 'AdminBlogManagementPage', { error });
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async (blogId: string) => {
    const ok = await confirm({ title: 'Archive this blog?', message: 'It will be removed from public view.', confirmLabel: 'Archive' });
    if (!ok) return;

    try {
      const response = await adminApiService.archiveBlog(blogId);
      if (response.success && response.data) {
        toast.success('Blog archived successfully');
        await loadBlogs();
      } else {
        toast.error(response.error?.message || 'Failed to archive blog');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
      logger.error('Failed to archive blog', 'AdminBlogManagementPage', { error });
    }
  };

  const handleUnarchive = async (blogId: string) => {
    try {
      const response = await adminApiService.unarchiveBlog(blogId);
      if (response.success && response.data) {
        toast.success('Blog unarchived successfully');
        await loadBlogs();
      } else {
        toast.error(response.error?.message || 'Failed to unarchive blog');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
      logger.error('Failed to unarchive blog', 'AdminBlogManagementPage', { error });
    }
  };

  const filteredBlogs = blogs.filter(blog => {
    if (filter === 'all') return true;
    if (filter === 'published') return blog.status === 'published' && !blog.is_archived;
    if (filter === 'draft') return blog.status === 'draft' && !blog.is_archived;
    if (filter === 'archived') return blog.is_archived;
    return true;
  });

  if (loading) {
    return (
      <div className={`${theme.card} p-8 text-center`}>
        <div className={`animate-spin rounded-full h-8 w-8 border-b-2 mx-auto ${theme.textAccent}`}></div>
        <p className={`mt-4 ${theme.textSecondary}`}>Loading blogs...</p>
      </div>
    );
  }

  return (
    <div>
      <ConfirmDialog />
      <h2 className={`text-2xl font-bold mb-6 ${theme.textHeading}`}>Blog Management</h2>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6">
        {(['all', 'published', 'draft', 'archived'] as const).map((filterOption) => (
          <button
            key={filterOption}
            onClick={() => setFilter(filterOption)}
            className={`px-4 py-2 rounded-xl transition capitalize ${
              filter === filterOption
                ? `${theme.buttonPrimary} ${theme.buttonTextPrimary}`
                : `${theme.buttonSecondary} ${theme.textSecondary}`
            }`}
          >
            <span className={filter === filterOption ? theme.textPrimary : theme.textSecondary}>
              {filterOption}
            </span>
          </button>
        ))}
      </div>

      {/* Blog List */}
      <div className={`${theme.card} rounded-3xl p-6`}>
        <h3 className={`text-xl font-semibold mb-4 ${theme.textHeading}`}>All Blogs</h3>
        <p className={`text-sm mb-4 ${theme.textSecondary}`}>Manage all blogs in this project</p>

        {filteredBlogs.length === 0 ? (
          <p className={`text-center py-8 ${theme.textMuted}`}>No blogs found</p>
        ) : (
          <div className="space-y-4">
            {filteredBlogs.map(blog => (
              <div
                key={blog.id}
                className={`p-4 rounded-lg border border-opacity-10 transition-all duration-300 ${
                  blog.is_archived ? 'opacity-60' : ''
                } ${theme.textPrimary}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className={`font-semibold text-lg mb-2 ${theme.textHeading}`}>{blog.title}</h4>
                    <p className={`text-sm mb-2 ${theme.textSecondary}`}>
                      By {blog.author?.username || 'Unknown'}
                    </p>
                    <div className="flex gap-4 text-xs">
                      <span className={theme.textMuted}>
                        Status: <span className={theme.textSecondary}>{blog.status}</span>
                      </span>
                      {blog.is_archived && (
                        <span className={theme.textMuted}>
                          Archived: {blog.archived_at ? dateUtils.format(blog.archived_at, 'SHORT') : 'Unknown'}
                        </span>
                      )}
                      <span className={theme.textMuted}>
                        Created: {dateUtils.format(blog.created_at, 'SHORT')}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Link
                      to={ROUTES.BLOG_POST(blog.slug || blog.id)}
                      className={`px-3 py-2 rounded-xl transition ${theme.buttonSecondary} ${theme.buttonTextSecondary}`}
                    >
                      <span className={theme.textSecondary}>View</span>
                    </Link>
                    {blog.is_archived ? (
                      <button
                        onClick={() => handleUnarchive(blog.id)}
                        className={`px-3 py-2 rounded-xl transition ${theme.buttonSecondary} ${theme.buttonTextSecondary}`}
                      >
                        <span className={theme.textSecondary}>Unarchive</span>
                      </button>
                    ) : (
                      <button
                        onClick={() => handleArchive(blog.id)}
                        className={`px-3 py-2 rounded-xl transition ${theme.buttonSecondary} ${theme.buttonTextSecondary}`}
                      >
                        <span className={theme.textSecondary}>Archive</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBlogManagementPage;

